import { Maximize2Icon, SparklesIcon, XIcon } from 'lucide-react'
import { lazy, Suspense, useState } from 'react'
import type { ReactNode } from 'react'

import {
  AppShellSidePanel,
  ButtonGroup,
  DelayedSpinner,
  IconButton,
  Separator,
  useSidebarResize,
} from '../../../core'
import { useAskAi } from './AskAiContext'

const LazyGlobalAgentChatLeftSidebarContent = lazy(async () => {
  const m = await import('./GlobalAgentChat')
  return { default: m.GlobalAgentChatLeftSidebarContent }
})

const GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_WIDTH_STORAGE_KEY =
  'global-agent-chat-left-sidebar:width'
const GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_DEFAULT_WIDTH = '22rem'
const GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_MIN_WIDTH = '18rem'
const GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_MAX_WIDTH = '60rem'

export function GlobalAgentChatSidePanel() {
  const askAi = useAskAi()

  if (askAi.state.layout !== 'sidebar' || !askAi.state.isOpen) {
    return null
  }

  return (
    <GlobalAgentChatResizableSidePanel>
      <GlobalAgentChatSidePanelContent />
    </GlobalAgentChatResizableSidePanel>
  )
}

function GlobalAgentChatResizableSidePanel({
  children,
}: {
  readonly children: ReactNode
}) {
  const [sidePanelWidth, setSidePanelWidth] = useState(() => {
    return (
      localStorage.getItem(GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_WIDTH_STORAGE_KEY) ??
      GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_DEFAULT_WIDTH
    )
  })
  const [isDraggingRail, setIsDraggingRail] = useState(false)
  const { dragRef, handleMouseDown } = useSidebarResize({
    currentWidth: sidePanelWidth,
    direction: 'right',
    enableToggle: false,
    isNested: true,
    maxResizeWidth: GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_MAX_WIDTH,
    minResizeWidth: GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_MIN_WIDTH,
    onResize: setSidePanelWidth,
    setDraggingRail: setIsDraggingRail,
    widthLocalStorageKey: GLOBAL_AGENT_CHAT_LEFT_SIDEBAR_WIDTH_STORAGE_KEY,
  })

  return (
    <AppShellSidePanel
      className="group relative min-h-0 overflow-hidden"
      data-dragging={isDraggingRail}
      style={{ width: sidePanelWidth }}
    >
      {children}
      {/* oxlint-disable-next-line joyful/no-plain-button */}
      <button
        ref={dragRef}
        type="button"
        aria-label={`Resize sidebar`}
        title={`Resize sidebar`}
        tabIndex={-1}
        className="hover:after:bg-sidebar-border group-data-[dragging=true]:after:bg-sidebar-border absolute inset-y-0 right-0 z-20 hidden w-1 cursor-ew-resize after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 group-data-[dragging=true]:after:w-1 sm:flex"
        onMouseDown={handleMouseDown}
      />
    </AppShellSidePanel>
  )
}

function GlobalAgentChatSidePanelContent() {
  const askAi = useAskAi()

  return (
    <aside
      className="bg-tremor-background-muted flex size-full flex-col overflow-hidden"
      aria-label="Ask AI"
    >
      <header className="flex h-12 shrink-0 items-center gap-x-2 px-4">
        <SparklesIcon className="size-4 shrink-0" />
        <span className="flex-1 truncate text-sm font-medium">{`Ask AI`}</span>
        <ButtonGroup density="compact">
          <IconButton
            size="sm"
            variant="ghost"
            tooltip={`Open as dialog`}
            onClick={() => {
              askAi.actions.setLayout('dialog')
            }}
          >
            <Maximize2Icon />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            tooltip={`Close`}
            onClick={() => {
              askAi.actions.close()
            }}
          >
            <XIcon />
          </IconButton>
        </ButtonGroup>
      </header>
      <Separator />
      <Suspense fallback={<DelayedSpinner height="fill" />}>
        <LazyGlobalAgentChatLeftSidebarContent shouldFocusInputOnMount />
      </Suspense>
    </aside>
  )
}
